import { isEqual } from 'lodash';

import {
  WorkflowsCreatePayloadArtifactParallelConfig,
  WorkflowsUpdateArtifactConfigPayload,
  WorkflowsUpdateArtifactPayload,
  WorkflowsUpdatePayload,
} from '@api/types.gen';

import {
  StaggerConfig,
  StaggerOption,
} from '@/react/edge/edge-stacks/components/StaggerFieldset.types';

import { WorkflowArtifact } from '../types';
import { Artifact, ArtifactConfig, FormValues } from '../wizard/types';
import {
  toEnvVarsPayload,
  toFilesPayload,
  toParallelDeploymentConfig,
} from '../wizard/payload';

/**
 * Builds a partial update from what the user actually changed: unchanged fields
 * and entirely unchanged artifacts are omitted, and the endpoint leaves anything
 * omitted alone. Artifacts are matched to `workflow.artifacts` by position,
 * which is how `toEditFormValues` laid them out.
 */
export function buildUpdateWorkflowPayload(
  values: FormValues,
  initialValues: FormValues,
  artifacts: Array<WorkflowArtifact>
): WorkflowsUpdatePayload {
  const changedArtifacts = artifacts
    .map((artifact, index) =>
      toArtifactUpdate(
        artifact,
        values.artifacts[index],
        initialValues.artifacts[index]
      )
    )
    .filter(isArtifactUpdate);

  return {
    name: changed(values.name, initialValues.name),
    artifacts: changedArtifacts.length > 0 ? changedArtifacts : undefined,
  };
}

function toArtifactUpdate(
  artifact: WorkflowArtifact,
  values: Artifact | undefined,
  initialValues: Artifact | undefined
): WorkflowsUpdateArtifactPayload | undefined {
  if (!values || !initialValues) {
    return undefined;
  }

  // The artifact name is deliberately absent: the endpoint cannot update it.
  const update = {
    files: changed(
      toFilesPayload(values.files),
      toFilesPayload(initialValues.files)
    ),
    targets: changed(
      { edgeGroups: values.targets },
      { edgeGroups: initialValues.targets }
    ),
    deploymentType: changed(
      values.deploymentType,
      initialValues.deploymentType
    ),
    config: toConfigUpdate(values.config, initialValues.config),
  };

  if (!hasChange(update)) {
    return undefined;
  }

  return { id: artifact.id, type: artifact.type, ...update };
}

function toConfigUpdate(
  values: ArtifactConfig,
  initialValues: ArtifactConfig
): WorkflowsUpdateArtifactConfigPayload | undefined {
  const update = {
    prePullImage: changed(values.prePullImage, initialValues.prePullImage),
    retryPeriod: changed(toRetryPeriod(values), toRetryPeriod(initialValues)),
    envVars: changed(
      toEnvVarsPayload(values.envVars),
      toEnvVarsPayload(initialValues.envVars)
    ),
    registries: changed(
      toRegistries(values.registryId),
      toRegistries(initialValues.registryId)
    ),
    parallelConfig: changed(
      toParallelConfig(values.parallelConfig),
      toParallelConfig(initialValues.parallelConfig)
    ),
  };

  return hasChange(update) ? update : undefined;
}

/**
 * The endpoint derives "retry enabled" from the period alone, so a disabled
 * retry has to be sent as zero rather than omitted.
 */
function toRetryPeriod(config: ArtifactConfig): number {
  return config.retryDeploy ? config.retryPeriod : 0;
}

/** An empty list clears the artifact's registries. */
function toRegistries(registryId: number | undefined): Array<number> {
  return registryId ? [registryId] : [];
}

/** A zero batch count switches the artifact back to all-at-once. */
function toParallelConfig(
  config: StaggerConfig
): WorkflowsCreatePayloadArtifactParallelConfig {
  return config.StaggerOption === StaggerOption.Parallel
    ? toParallelDeploymentConfig(config)
    : { batchCount: 0 };
}

function changed<T>(value: T, initialValue: T): T | undefined {
  return isEqual(value, initialValue) ? undefined : value;
}

function hasChange(update: object): boolean {
  return Object.values(update).some((value) => value !== undefined);
}

function isArtifactUpdate(
  update: WorkflowsUpdateArtifactPayload | undefined
): update is WorkflowsUpdateArtifactPayload {
  return !!update;
}
